import { Fragment, useCallback, useEffect, useState } from 'react';
import { FaCheck, FaLock } from 'react-icons/fa';
import { CustomButton } from '../../../components/CustomButton';
import RModal from '../../../components/Rsuite/Modal';
import DadosPromocao from './Dados';
import HorariosPromocao from './Horarios';
import RegrasPromocao from './Regras';
import BeneficiosPromocao from './Beneficios';
import {
  ContainerModal,
  TabsContainer,
  TabContent,
  StepperContainer,
  StepButton,
  StepCircle,
  StepLabel,
  StepLine,
  FooterBar,
} from './styles';
import api from '../../../services/api';
import { useAuth } from '../../../hooks/auth';
import { useAlert } from '../../../contexts/Alert.context';
import { toastSuccess, toastError, toastWarning } from '../../../toasts';
import {
  promotionsDateTypes,
  promotionsTypes,
} from '../../../enums/promotionsTypes';
import { normalizeDateTime } from './utils';

const tiposComBeneficios = [
  promotionsTypes.COMPRE_E_GANHE,
  promotionsTypes.BORDAS,
  promotionsTypes.DESCONTO_TAMANHO,
];

const tiposComHorarios = [
  promotionsDateTypes.DATA_FIXA,
  promotionsDateTypes.DATA_PERIODO,
];

const EditarCadastrarPromocao = ({
  open,
  close,
  promocao,
  setPromocao,
  setPromocoes,
  produtos,
  bordas,
  tamanhos,
  setIsBorderPromo,
}) => {
  const { id_empresa } = useAuth();
  const { showAlert } = useAlert();
  const [activeTab, setActiveTab] = useState('dados');
  const [ativo, setAtivo] = useState(0);
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipoPromocao, setTipoPromocao] = useState('');
  const [tipoData, setTipoData] = useState();
  const [valor, setValor] = useState(0);
  const [condicaoValorMin, setCondicaoValorMin] = useState(0);
  const [inicio, setInicio] = useState(null);
  const [fim, setFim] = useState(null);
  const [qtdHorariosAtivos, setQtdHorariosAtivos] = useState(null);
  const [ativarAposHorarios, setAtivarAposHorarios] = useState(false);
  const [disableButton, setDisableButton] = useState(false);

  const isPeriodo = tipoData === promotionsDateTypes.DATA_PERIODO;
  const isDataFixa = tipoData === promotionsDateTypes.DATA_FIXA;

  const validarDados = useCallback(() => {
    let erro = null;

    if (!titulo?.trim()) erro = 'Informe o título da promoção.';
    else if (!tipoPromocao) erro = 'Selecione o tipo da promoção.';
    else if (tipoPromocao === promotionsTypes.LEVE_X_PAGUE_Y && +valor <= 0)
      erro = 'Informe o número de itens pagos.';
    else if (!tipoData) erro = 'Selecione o tipo de data da promoção.';

    if (erro) {
      toastError(erro);
      setActiveTab('dados');
      return false;
    }
    return true;
  }, [tipoData, tipoPromocao, titulo, valor]);

  const validarPeriodo = useCallback(() => {
    if (!isPeriodo) return true;

    let erro = null;
    if (!inicio || !fim)
      erro = 'Informe a data e hora de início e de término do período.';
    else if (normalizeDateTime(fim) <= normalizeDateTime(inicio))
      erro = 'A data final deve ser posterior à data de início.';

    if (erro) {
      toastError(erro);
      setActiveTab('horarios');
      return false;
    }
    return true;
  }, [fim, inicio, isPeriodo]);

  const contarHorariosAtivos = useCallback(async (idPromocao) => {
    const response = await api.get(`horarios-promocao/listar/${idPromocao}`);
    return response.data.filter((h) => h.Ativo).length;
  }, []);

  const salvarPromocao = useCallback(async () => {
    if (!validarDados() || !validarPeriodo()) return null;

    let ativoEnviado = +ativo;
    if (isDataFixa && promocao && ativoEnviado) {
      const qtd = await contarHorariosAtivos(promocao.Id).catch(() => 0);
      if (!qtd) {
        ativoEnviado = 0;
        setAtivarAposHorarios(true);
        toastWarning(
          'A promoção ficará inativa até ter ao menos um horário cadastrado.',
        );
      }
    }

    const payload = {
      Titulo: titulo.trim(),
      Descricao: descricao,
      Tipo: tipoPromocao,
      Valor: valor,
      CondicaoValorMinimo: condicaoValorMin,
      DataPromocao: tipoData,
      DataInicio: isPeriodo ? inicio : null,
      DataFim: isPeriodo ? fim : null,
    };

    try {
      if (promocao) {
        const response = await api.patch(`promocao/alterar/${promocao.Id}`, {
          ...payload,
          Ativo: ativoEnviado,
        });
        setPromocoes((state) =>
          state.map((e) => (e.Id !== response.data.Id ? e : response.data)),
        );
        setPromocao(response.data);
        return response.data;
      }

      const response = await api.post('promocao/criar', {
        ...payload,
        IdEmpresa: id_empresa,
      });
      if (isDataFixa && tipoPromocao !== promotionsTypes.COMBO_PRECO_FIXO) {
        setAtivarAposHorarios(true);
      }
      setPromocoes((state) => [...state, response.data]);
      setPromocao(response.data);
      return response.data;
    } catch (error) {
      toastError(
        error?.response?.data?.Msg ||
          (promocao
            ? 'Não foi possível salvar a promoção.'
            : 'Não foi possível cadastrar a promoção.'),
      );
      return null;
    }
  }, [
    ativo,
    condicaoValorMin,
    contarHorariosAtivos,
    descricao,
    fim,
    id_empresa,
    inicio,
    isDataFixa,
    isPeriodo,
    promocao,
    setPromocao,
    setPromocoes,
    tipoData,
    tipoPromocao,
    titulo,
    validarDados,
    validarPeriodo,
    valor,
  ]);

  useEffect(() => {
    if (!isDataFixa) setAtivarAposHorarios(false);
  }, [isDataFixa]);

  const garantirPromocaoSalva = useCallback(async () => {
    if (promocao && promocao.DataPromocao === tipoData) return promocao;
    return salvarPromocao();
  }, [promocao, salvarPromocao, tipoData]);

  useEffect(() => {
    if (promocao) {
      setTitulo(promocao.Titulo ?? '');
      setDescricao(promocao.Descricao ?? '');
      setTipoPromocao(promocao.Tipo);
      setTipoData(promocao.DataPromocao);
      setValor(+promocao.Valor);
      setCondicaoValorMin(+promocao.CondicaoValorMinimo);
      setInicio(promocao.DataInicio);
      setFim(promocao.DataFim);
      setAtivo(+promocao.Ativo);
    } else {
      setActiveTab('dados');
    }
  }, [open, promocao]);

  const ativarPromocao = useCallback(async () => {
    try {
      const response = await api.patch(`promocao/alterar/${promocao.Id}`, {
        Ativo: 1,
      });
      setPromocoes((state) =>
        state.map((e) => (e.Id !== response.data.Id ? e : response.data)),
      );
      setPromocao(response.data);
      setAtivarAposHorarios(false);
      toastSuccess('Horários definidos. Promoção ativada!');
      return true;
    } catch (error) {
      toastError(
        error?.response?.data?.Msg || 'Não foi possível ativar a promoção.',
      );
      return false;
    }
  }, [promocao, setPromocao, setPromocoes]);

  useEffect(() => {
    setIsBorderPromo(tipoPromocao === promotionsTypes.BORDAS);
  }, [setIsBorderPromo, tipoPromocao]);

  const steps = [
    { key: 'dados', label: 'Dados Gerais' },
    tiposComHorarios.includes(tipoData) && {
      key: 'horarios',
      label: isDataFixa ? 'Dias e Horários' : 'Período',
      requiresPromo: isDataFixa,
    },
    { key: 'regras', label: 'Produtos Participantes', requiresPromo: true },
    tiposComBeneficios.includes(tipoPromocao) && {
      key: 'beneficios',
      label: 'Benefícios (Prêmios)',
      requiresPromo: true,
    },
  ].filter(Boolean);

  const activeStepIndex = steps.findIndex((step) => step.key === activeTab);
  const isLastStep = activeStepIndex === steps.length - 1;
  const nextStep = steps[activeStepIndex + 1];
  const prevStep = steps[activeStepIndex - 1];

  useEffect(() => {
    if (activeStepIndex === -1) setActiveTab('dados');
  }, [activeStepIndex]);

  const hasUnsavedChanges = promocao
    ? (titulo ?? '') !== (promocao.Titulo ?? '') ||
      (descricao ?? '') !== (promocao.Descricao ?? '') ||
      tipoPromocao !== promocao.Tipo ||
      tipoData !== promocao.DataPromocao ||
      +valor !== +promocao.Valor ||
      +condicaoValorMin !== +promocao.CondicaoValorMinimo ||
      +ativo !== +promocao.Ativo ||
      (isPeriodo &&
        (normalizeDateTime(inicio) !== normalizeDateTime(promocao.DataInicio) ||
          normalizeDateTime(fim) !== normalizeDateTime(promocao.DataFim)))
    : Boolean(titulo || descricao || tipoPromocao);

  const pendenteHorarios =
    Boolean(promocao) && isDataFixa && ativarAposHorarios && !+promocao.Ativo;

  const fechar = () => {
    const avisos = [
      hasUnsavedChanges && 'Existem alterações não salvas.',
      pendenteHorarios &&
        'A promoção está inativa porque ainda não possui horários cadastrados.',
    ].filter(Boolean);

    if (!avisos.length) {
      close();
      return;
    }

    showAlert({
      title: 'Sair sem concluir?',
      text: `${avisos.join(' ')} Deseja sair mesmo assim?`,
      onSubmit: close,
      submitText: 'Sair',
    });
  };

  const irPara = (key) => {
    if (key) setActiveTab(key);
    else close();
  };

  const avancar = async () => {
    setDisableButton(true);
    try {
      if (activeTab === 'dados') {
        if (!validarDados()) return;

        if (isPeriodo && (!inicio || !fim)) {
          setActiveTab('horarios');
          return;
        }

        const eraNova = !promocao;
        const salva = await salvarPromocao();
        if (!salva) return;

        if (eraNova && tipoPromocao === promotionsTypes.COMBO_PRECO_FIXO) {
          toastSuccess(
            'Promoção cadastrada como inativa. Adicione os produtos do combo e ative-a em "Dados Gerais".',
          );
        } else {
          toastSuccess(
            eraNova ? 'Promoção cadastrada!' : 'Promoção salva com sucesso.',
          );
        }
        irPara(nextStep?.key);
        return;
      }

      if (activeTab === 'horarios') {
        if (isDataFixa && qtdHorariosAtivos === null) return;
        if (isDataFixa && qtdHorariosAtivos === 0) {
          toastError('Adicione ao menos um dia/horário ativo para a promoção.');
          return;
        }

        const eraNova = !promocao;
        if (hasUnsavedChanges || eraNova) {
          const salva = await salvarPromocao();
          if (!salva) return;
          toastSuccess(
            eraNova ? 'Promoção cadastrada!' : 'Promoção salva com sucesso.',
          );
        }

        if (pendenteHorarios) await ativarPromocao();
        irPara(nextStep?.key);
        return;
      }

      irPara(nextStep?.key);
    } finally {
      setDisableButton(false);
    }
  };

  const salvarDadosLabel = promocao ? 'Salvar' : 'Criar promoção';
  const primaryLabel = (() => {
    if (activeTab === 'dados') {
      if (isPeriodo && (!inicio || !fim)) return 'Continuar';
      return isLastStep
        ? `${salvarDadosLabel} e concluir`
        : `${salvarDadosLabel} e continuar`;
    }
    if (activeTab === 'horarios' && (hasUnsavedChanges || !promocao)) {
      return isLastStep
        ? `${salvarDadosLabel} e concluir`
        : `${salvarDadosLabel} e continuar`;
    }
    return isLastStep ? 'Concluir' : 'Continuar';
  })();

  return (
    <RModal
      open={open}
      onClose={fechar}
      size="md"
      title={
        promocao ? `Configurar Promoção: ${promocao.Titulo}` : `Nova Promoção`
      }
      footer={
        <FooterBar>
          <div className="footer-group">
            {hasUnsavedChanges && promocao && (
              <span className="footer-hint">Alterações não salvas</span>
            )}
          </div>
          <div className="footer-group">
            <CustomButton onClick={fechar} appearance="ghost">
              {promocao && !hasUnsavedChanges ? 'Fechar' : 'Cancelar'}
            </CustomButton>
            {prevStep && (
              <CustomButton
                appearance="default"
                onClick={() => setActiveTab(prevStep.key)}
                disabled={disableButton}
              >
                Voltar
              </CustomButton>
            )}
            <CustomButton
              disabled={disableButton}
              loading={disableButton}
              onClick={avancar}
              appearance="primary"
            >
              {primaryLabel}
            </CustomButton>
          </div>
        </FooterBar>
      }
    >
      <ContainerModal>
        <TabsContainer>
          <StepperContainer>
            {steps.map((step, index) => {
              const locked = step.requiresPromo && !promocao;
              const status =
                index === activeStepIndex
                  ? 'active'
                  : index < activeStepIndex
                    ? 'done'
                    : 'pending';

              return (
                <Fragment key={step.key}>
                  <StepButton
                    type="button"
                    disabled={locked}
                    title={
                      locked
                        ? 'Salve os dados gerais para liberar esta etapa'
                        : undefined
                    }
                    onClick={() => setActiveTab(step.key)}
                  >
                    <StepCircle status={status}>
                      {locked ? (
                        <FaLock size={10} />
                      ) : status === 'done' ? (
                        <FaCheck size={12} />
                      ) : (
                        index + 1
                      )}
                    </StepCircle>
                    <StepLabel status={status}>{step.label}</StepLabel>
                  </StepButton>
                  {index < steps.length - 1 && (
                    <StepLine filled={index < activeStepIndex} />
                  )}
                </Fragment>
              );
            })}
          </StepperContainer>

          <TabContent>
            {activeTab === 'dados' && (
              <DadosPromocao
                titulo={titulo}
                setTitulo={setTitulo}
                descricao={descricao}
                setDescricao={setDescricao}
                tipoPromocao={tipoPromocao}
                setTipoPromocao={setTipoPromocao}
                valor={valor}
                setValor={setValor}
                condicaoValorMin={condicaoValorMin}
                setCondicaoValorMin={setCondicaoValorMin}
                tipoData={tipoData}
                setTipoData={setTipoData}
                ativo={ativo}
                setAtivo={setAtivo}
                promocao={promocao}
              />
            )}

            {activeTab === 'horarios' && (
              <HorariosPromocao
                promo={promocao}
                tipoData={tipoData}
                inicio={inicio}
                fim={fim}
                setInicio={setInicio}
                setFim={setFim}
                ensurePromocao={garantirPromocaoSalva}
                onSchedulesCountChange={setQtdHorariosAtivos}
                pendenteAtivacao={pendenteHorarios}
              />
            )}

            {activeTab === 'regras' && (
              <RegrasPromocao promo={promocao} products={produtos} />
            )}

            {activeTab === 'beneficios' && (
              <BeneficiosPromocao
                promo={promocao}
                products={
                  tipoPromocao === promotionsTypes.BORDAS
                    ? bordas
                    : tipoPromocao === promotionsTypes.DESCONTO_TAMANHO
                      ? tamanhos
                      : produtos
                }
              />
            )}
          </TabContent>
        </TabsContainer>
      </ContainerModal>
    </RModal>
  );
};

export default EditarCadastrarPromocao;
